/**
 * Example Preview Component
 *
 * Modal showing detailed information about an example file,
 * including learning guidance and object exploration.
 */

import { useState, useCallback } from 'react';
import type { ExampleFileMetadata, ExamplePreviewTab } from '../types';
import { COMPLEXITY_LEVELS, EXAMPLE_CATEGORIES, HVAC_SYSTEM_TYPES, LEARNING_CONCEPTS } from '../types';
import { getRelatedExamples } from '../services/exampleSearch';

export interface ExamplePreviewProps {
  example: ExampleFileMetadata;
  onClose: () => void;
  onSelect: () => void;
  onNavigate?: (exampleId: string) => void;
}

export function ExamplePreview({ example, onClose, onSelect, onNavigate }: ExamplePreviewProps) {
  const [activeTab, setActiveTab] = useState<ExamplePreviewTab>('overview');
  const [currentStep, setCurrentStep] = useState(0);

  const complexityInfo = COMPLEXITY_LEVELS[example.complexity];
  const categoryInfo = EXAMPLE_CATEGORIES[example.category];
  const relatedExamples = getRelatedExamples(example.id, 4);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  const complexityColors: Record<string, string> = {
    beginner: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    intermediate: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    advanced: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    expert: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-background shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between border-b p-4">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">{example.name}</h2>
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${complexityColors[example.complexity]}`}>
                {complexityInfo.name}
              </span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{example.filename}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-2 hover:bg-muted"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          {(['overview', 'learning', 'objects'] as ExamplePreviewTab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${
                activeTab === tab
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab === 'learning' ? 'Learning Guide' : tab}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'overview' && (
            <OverviewTab example={example} categoryInfo={categoryInfo} />
          )}

          {activeTab === 'learning' && (
            <LearningTab
              example={example}
              currentStep={currentStep}
              onStepChange={setCurrentStep}
            />
          )}

          {activeTab === 'objects' && <ObjectsTab example={example} />}
        </div>

        {/* Related Examples */}
        {relatedExamples.length > 0 && (
          <div className="border-t p-4">
            <h4 className="mb-2 text-sm font-medium">Related Examples</h4>
            <div className="flex flex-wrap gap-2">
              {relatedExamples.map((related) => (
                <button
                  key={related.id}
                  onClick={() => onNavigate?.(related.id)}
                  className="rounded-md border px-3 py-1 text-sm hover:bg-muted"
                >
                  {related.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between border-t p-4">
          <div className="text-sm text-muted-foreground">
            {example.weatherFile && (
              <span>Recommended weather: {example.weatherFile.name}</span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
            >
              Cancel
            </button>
            <button
              onClick={onSelect}
              className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
            >
              Open in Editor
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function OverviewTab({
  example,
  categoryInfo,
}: {
  example: ExampleFileMetadata;
  categoryInfo: { name: string; description: string };
}) {
  return (
    <div className="space-y-6">
      {/* Description */}
      <div>
        <h3 className="mb-2 font-medium">Description</h3>
        <p className="text-muted-foreground">{example.description}</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard label="Category" value={categoryInfo.name} />
        {example.stats && (
          <>
            <StatCard label="Zones" value={example.stats.zones.toString()} />
            <StatCard label="Surfaces" value={example.stats.surfaces.toString()} />
            {example.stats.objectCount && (
              <StatCard label="Objects" value={example.stats.objectCount.toString()} />
            )}
          </>
        )}
      </div>

      {/* HVAC Systems */}
      {example.hvacSystems && example.hvacSystems.length > 0 && (
        <div>
          <h3 className="mb-2 font-medium">HVAC Systems</h3>
          <div className="flex flex-wrap gap-2">
            {example.hvacSystems.map((sys) => (
              <span
                key={sys}
                className="rounded-md bg-purple-100 px-2 py-1 text-sm text-purple-800 dark:bg-purple-900/30 dark:text-purple-300"
              >
                {HVAC_SYSTEM_TYPES[sys]?.name ?? sys}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Tags */}
      <div>
        <h3 className="mb-2 font-medium">Tags</h3>
        <div className="flex flex-wrap gap-2">
          {example.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-md bg-gray-100 px-2 py-1 text-sm dark:bg-gray-800"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* Source */}
      <div>
        <h3 className="mb-2 font-medium">Source</h3>
        <p className="text-sm text-muted-foreground">
          {example.source.origin} v{example.source.version}
          {example.source.author && ` - ${example.source.author}`}
        </p>
      </div>
    </div>
  );
}

function LearningTab({
  example,
  currentStep,
  onStepChange,
}: {
  example: ExampleFileMetadata;
  currentStep: number;
  onStepChange: (step: number) => void;
}) {
  const learning = example.learning;
  const steps = learning.steps ?? [];

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="rounded-lg bg-primary/5 p-4">
        <h3 className="mb-2 flex items-center gap-2 font-medium">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-primary"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
          What You'll Learn
        </h3>
        <p className="text-muted-foreground">{learning.summary}</p>
      </div>

      {/* Prerequisites */}
      {learning.prerequisites && learning.prerequisites.length > 0 && (
        <div>
          <h3 className="mb-2 font-medium">Prerequisites</h3>
          <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
            {learning.prerequisites.map((prereq, i) => (
              <li key={i}>{prereq}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Concepts Covered */}
      <div>
        <h3 className="mb-2 font-medium">Concepts Covered</h3>
        <div className="flex flex-wrap gap-2">
          {learning.concepts.map((concept) => (
            <span
              key={concept}
              className="rounded-md bg-blue-100 px-2 py-1 text-sm text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
            >
              {LEARNING_CONCEPTS[concept]?.name ?? concept}
            </span>
          ))}
        </div>
      </div>

      {/* Step-by-Step Guide */}
      {steps.length > 0 && (
        <div>
          <h3 className="mb-3 font-medium">Step-by-Step Guide</h3>
          <div className="space-y-4">
            {/* Step indicators */}
            <div className="flex items-center gap-2">
              {steps.map((_, i) => (
                <button
                  key={i}
                  onClick={() => onStepChange(i)}
                  className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors ${
                    i === currentStep
                      ? 'bg-primary text-primary-foreground'
                      : i < currentStep
                      ? 'bg-primary/20 text-primary'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>

            {/* Current step content */}
            <div className="rounded-lg border p-4">
              <h4 className="mb-2 font-medium">{steps[currentStep]?.title}</h4>
              <p className="mb-4 text-muted-foreground">{steps[currentStep]?.description}</p>

              {/* Focus objects */}
              {steps[currentStep]?.focusObjects && steps[currentStep]?.focusObjects.length > 0 && (
                <div className="mb-4">
                  <span className="text-sm font-medium">Key Objects:</span>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {steps[currentStep]?.focusObjects?.map((obj) => (
                      <code
                        key={obj}
                        className="rounded bg-gray-100 px-2 py-0.5 text-sm dark:bg-gray-800"
                      >
                        {obj}
                      </code>
                    ))}
                  </div>
                </div>
              )}

              {/* Tips */}
              {steps[currentStep]?.tips && steps[currentStep]?.tips.length > 0 && (
                <div className="rounded-md bg-yellow-50 p-3 dark:bg-yellow-900/20">
                  <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                    Tips:
                  </span>
                  <ul className="mt-1 list-inside list-disc space-y-1 text-sm text-yellow-700 dark:text-yellow-300">
                    {steps[currentStep]?.tips?.map((tip, i) => (
                      <li key={i}>{tip}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Navigation */}
              <div className="mt-4 flex justify-between">
                <button
                  onClick={() => onStepChange(Math.max(0, currentStep - 1))}
                  disabled={currentStep === 0}
                  className="rounded-md px-3 py-1 text-sm hover:bg-muted disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => onStepChange(Math.min(steps.length - 1, currentStep + 1))}
                  disabled={currentStep === steps.length - 1}
                  className="rounded-md px-3 py-1 text-sm hover:bg-muted disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Common Mistakes */}
      {learning.commonMistakes && learning.commonMistakes.length > 0 && (
        <div>
          <h3 className="mb-2 font-medium">Common Mistakes to Avoid</h3>
          <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
            {learning.commonMistakes.map((mistake, i) => (
              <li key={i}>{mistake}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Try This */}
      {learning.tryThis && learning.tryThis.length > 0 && (
        <div className="rounded-lg bg-green-50 p-4 dark:bg-green-900/20">
          <h3 className="mb-2 font-medium text-green-800 dark:text-green-200">
            Try These Modifications
          </h3>
          <ul className="list-inside list-disc space-y-1 text-sm text-green-700 dark:text-green-300">
            {learning.tryThis.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ObjectsTab({ example }: { example: ExampleFileMetadata }) {
  return (
    <div className="space-y-6">
      {/* Primary Objects */}
      <div>
        <h3 className="mb-2 font-medium">Primary Objects (Focus of this example)</h3>
        <div className="space-y-2">
          {example.objectsShowcased.primary.map((obj) => (
            <div
              key={obj}
              className="flex items-center justify-between rounded-lg border p-3"
            >
              <code className="font-mono text-sm">{obj}</code>
              <a
                href={`https://bigladdersoftware.com/epx/docs/24-1/input-output-reference/${obj.toLowerCase().split(':')[0]}.html`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline"
              >
                Documentation
              </a>
            </div>
          ))}
        </div>
      </div>

      {/* Secondary Objects */}
      {example.objectsShowcased.secondary && example.objectsShowcased.secondary.length > 0 && (
        <div>
          <h3 className="mb-2 font-medium">Supporting Objects</h3>
          <div className="flex flex-wrap gap-2">
            {example.objectsShowcased.secondary.map((obj) => (
              <code
                key={obj}
                className="rounded bg-gray-100 px-2 py-1 text-sm dark:bg-gray-800"
              >
                {obj}
              </code>
            ))}
          </div>
        </div>
      )}

      {/* Quick Reference */}
      <div className="rounded-lg bg-muted p-4">
        <h3 className="mb-2 font-medium">Quick Reference</h3>
        <p className="mb-2 text-sm text-muted-foreground">
          When you open this file, look for these key objects to understand how the system works.
        </p>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-primary" />
            <span>Search for object names using Ctrl+F in the editor</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-primary" />
            <span>Objects are organized by class in the Object Navigator</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-primary" />
            <span>Use the HVAC Diagram view to see system connections</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border p-3 text-center">
      <p className="text-2xl font-semibold">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}
